import { ExclamationTriangleIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useAuth } from 'context/AuthContext';
import { verifyAdminAccess, AdminAuthResult } from 'middleware/adminAuth';
import LoadingSpinner from '../ui/LoadingSpinner';

interface AdminAuthGuardProps {
  children: React.ReactNode;
  fallbackPath?: string;
  showErrorMessage?: boolean;
}

/**
 * Componente de protección para rutas administrativas
 * Proporciona verificación robusta de permisos administrativos
 */
const AdminAuthGuard: React.FC<AdminAuthGuardProps> = ({
  children,
  fallbackPath = '/dashboard',
  showErrorMessage = true
}) => {
  const { user, loading: authLoading } = useAuth();
  const [authResult, setAuthResult] = useState<AdminAuthResult | null>(null);
  const [isVerifying, setIsVerifying] = useState(true);

  useEffect(() => {
    const verifyAccess = async () => {
      if (authLoading) return;
      
      setIsVerifying(true);
      try {
        const result = await verifyAdminAccess(user);
        setAuthResult(result);
      } catch (error) {
        console.error('Error en verificación administrativa:', error);
        setAuthResult({
          isAuthorized: false,
          user,
          error: 'Error interno de verificación',
          redirectTo: fallbackPath
        });
      } finally {
        setIsVerifying(false);
      }
    };

    verifyAccess();
  }, [user, authLoading, fallbackPath]);

  // Mostrar loader mientras se verifica
  if (authLoading || isVerifying) {
    return (
      <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
        <LoadingSpinner
          size="lg"
          message="Verificando permisos administrativos..."
        />
      </div>
    );
  }

  // Si no hay resultado de verificación, mostrar error
  if (!authResult) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
          </div>
          <div className="mt-4 text-center">
            <h3 className="text-lg font-medium text-gray-900">Error de Verificación</h3>
            <p className="mt-2 text-sm text-gray-600">
              No se pudo verificar el acceso administrativo. Por favor, intenta nuevamente.
            </p>
            <div className="mt-6">
              <button
                onClick={() => window.location.href = fallbackPath}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                Volver al Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Si no está autorizado, redirigir o mostrar error
  if (!authResult.isAuthorized) {
    if (authResult.redirectTo) {
      return <Navigate to={authResult.redirectTo} replace />;
    }

    if (showErrorMessage) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-yellow-100 rounded-full">
              <ShieldExclamationIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="mt-4 text-center">
              <h3 className="text-lg font-medium text-gray-900">Acceso Restringido</h3>
              <p className="mt-2 text-sm text-gray-600">
                {authResult.error || 'No tienes permisos para acceder a esta sección.'}
              </p>
              <div className="mt-6 space-y-3">
                <button
                  onClick={() => window.location.href = fallbackPath}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                >
                  Volver al Dashboard
                </button>
                <button
                  onClick={() => window.location.href = '/auth/login'}
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Iniciar Sesión Nuevamente
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return <Navigate to={authResult.redirectTo || fallbackPath} replace />;
  }

  // Usuario autorizado, mostrar contenido
  return <>{children}</>;
};

export default AdminAuthGuard;
